import { FileDoneOutlined, UploadOutlined } from "@ant-design/icons"
import { Dropdown, type MenuProps } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { ActionTooltip } from "../../../components/ActionTooltip.tsx"

export type AddInvoiceKind = "generated" | "external"

interface AddInvoiceMenuProps {
  generateAllowed?: boolean
  externalAllowed?: boolean
  disabled?: boolean
  /** Tooltip for the trigger; it has to sit outside the menu to reach it. */
  tooltip?: string
  onSelect: (kind: AddInvoiceKind) => void
  /** The control that opens the menu. It receives the click itself. */
  children: React.ReactElement
}

/**
 * A drawer or modal is its own stacking context, so a menu parked on
 * `document.body` would open under the overlay it was clicked in.
 */
export const addInvoiceMenuContainer = (trigger: HTMLElement): HTMLElement =>
  (trigger.closest(".ant-drawer-body, .ant-modal-body") as HTMLElement | null) ??
  document.body

/**
 * The one way every screen adds an invoice: generate it in EZBuild, or record
 * one made elsewhere. With a single allowed option the choice is already made,
 * so the control opens that drawer directly rather than a menu of one.
 */
export function AddInvoiceMenu({
  generateAllowed = false,
  externalAllowed = false,
  disabled = false,
  tooltip,
  onSelect,
  children
}: AddInvoiceMenuProps) {
  const { t } = useTranslation()

  const kinds: AddInvoiceKind[] = [
    ...(generateAllowed ? (["generated"] as const) : []),
    ...(externalAllowed ? (["external"] as const) : [])
  ]

  if (!kinds.length) return null

  const withTooltip = (trigger: React.ReactElement) =>
    tooltip ? <ActionTooltip title={tooltip}>{trigger}</ActionTooltip> : trigger

  if (kinds.length === 1) {
    const [only] = kinds
    return withTooltip(
      React.cloneElement(children as React.ReactElement<{
        onClick?: React.MouseEventHandler
      }>, {
        onClick: (event: React.MouseEvent) => {
          event.stopPropagation()
          if (!disabled) onSelect(only)
        }
      })
    )
  }

  const items: MenuProps["items"] = [
    {
      key: "generated",
      icon: <FileDoneOutlined />,
      label: t("invoices.addMenu.generate")
    },
    {
      key: "external",
      icon: <UploadOutlined />,
      label: t("invoices.addMenu.external")
    }
  ]

  return withTooltip(
    <Dropdown
      trigger={disabled ? [] : ["click"]}
      placement="bottomRight"
      // Without focus in the menu, a keyboard user opens it and cannot move.
      autoFocus
      getPopupContainer={addInvoiceMenuContainer}
      menu={{
        items,
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation()
          onSelect(key as AddInvoiceKind)
        }
      }}
    >
      {children}
    </Dropdown>
  )
}
